import React, { useEffect, useState } from "react";
import Parse from "../../api/ApiConfig";
import { Outlet, useNavigate, useParams } from "react-router";
import AdminDashboardLayout from "../../layout/AdminDashboardLayout";
import { PageHeader } from "./Lease.styled";
import FilterCategory from "../../components/ReusableComponents/FilterCategory";
import { TabList1, TabList2 } from "./data";
import LeaseDataTable from "./sections/LeaseDataTable";
import { Backdrop, Button, CircularProgress } from "@mui/material";
import { fetchLeaseTable } from "./utils";
import { toast } from "react-toastify";
import LeaseCharts from "./partials/LeaseCharts";
import { StyledContainer } from "../../globalStyle/GlobalStyle";
import { useSearchParams } from "react-router-dom";
import { canCreateAsset } from "../../utils/UserAuth";
import { useDispatch } from "react-redux";
import { saveAdminQuickRequests, saveInvestorSponsoredRequests } from "./LeaseSlice";

function LeasePage() {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const [searchParams] = useSearchParams();
	let { category, id: LeaseId } = useParams();

	const [currentTab, setCurrentTab] = useState(category || "ongoing");
	const [profileFilter, setProfileFilter] = useState(getCurrentFilter(category, searchParams));
	const [defaultData, setDefaultData] = useState([]);
	const [TableData, setTableData] = useState([]);
	const [loading, setLoading] = useState(false);

	const getLeaseTable = async (category, filter) => {
		// Fetch Lease table data
		const data = await fetchLeaseTable(category || currentTab, filter || profileFilter);
		if (data) {
			setLoading(false);
			setTableData(data);
			setDefaultData(data);

			const quickLeaseRequests = data?.filter(
				(request) => request?.leaseType?.includes("Quick") && request?.leaseSource?.includes("market")
			);
			const sponsoredLeaseRequests = data?.filter(
				(request) => request?.leaseType?.includes("Sponsored") && request?.leaseSource?.includes("market")
			);

			dispatch(saveAdminQuickRequests(quickLeaseRequests));
			dispatch(saveInvestorSponsoredRequests(sponsoredLeaseRequests));
		} else {
			setLoading(false);
			setTableData([]);
			toast.error("Error fetching Lease!");
		}
	};

	useEffect(() => {
		// If current page is LeaseDashboard, fetch data
		if (!LeaseId) {
			setLoading(true);
			getLeaseTable();
		}
	}, []);

	useEffect(() => {
		console.log("useEffectCategory", category);
		if (category) {
			setCurrentTab(category);
		} else {
			// If no category is selected, redirect to ongoing
			navigate(`/lease/${currentTab}`, { replace: true });
		}
	}, [category]);

	const handleTabChange = (category, profile) => {
		setLoading(true);
		getLeaseTable(category, profile);
		navigate(`/lease/${category}/?filter=${profile || profileFilter}`);
	};

	const handleProfileFilter = (profile) => {
		setProfileFilter(profile);
		const tabList =
			profile === "personal_business"
				? TabList1
				: profile === "asset_registration"
				? [{ id: "completed" }]
				: profile === "insurance"
				? [{ id: "completed" }]
				: TabList2;
		handleTabChange(tabList[0].id, profile);
	};

	const handleDeleteLease = async (selected) => {
		if (selected.length) {
			setLoading(true);
			const LeaseObjects = selected.map((item) => {
				const LeaseObj = new Parse.Object("Lease");
				LeaseObj.id = item.objectId;
				return LeaseObj;
			});

			Parse.Object.destroyAll(LeaseObjects)
				.then((object) => {
					getLeaseTable();
					console.log("Lease Objects deleted successfully", object);
					toast.success("Lease Deleted Successfully");
				})
				.catch((error) => {
					setLoading(false);
					console.error("Error while deleting Lease", error);
					toast.error("Error deleting Lease");
				});
		} else {
			toast.error("No Item selected");
		}
	};

	const getTabList = (filter) => {
		if (location.pathname.includes("/lease/asset_registration")) {
			return [];
		} else if (location.pathname.includes("/lease/insurance")) {
			return [];
		}
		if (filter === "personal_business") {
			return TabList1;
		} else if (filter === "investor") {
			return TabList2;
		} else if (filter === "quick_lease") {
			return TabList2.slice(0, -1);
		} else {
			return [];
		}
	};

	return (
		<AdminDashboardLayout data={TableData} defaultData={defaultData} setData={setTableData}>
			<StyledContainer>
				<PageHeader>
					{!LeaseId && (
						<div className='topHeader flex gap-5'>
							<div className='flex scroll-wrapper' style={{ minWidth: "500px", overflowX: "auto" }}>
								<div
									className={`profileTypeBtn ${profileFilter === "personal_business" && "active"}`}
									onClick={() => handleProfileFilter("personal_business")}>
									Personal / Business
								</div>

								<div
									className={`profileTypeBtn ${profileFilter === "investor" && "active"}`}
									onClick={() => handleProfileFilter("investor")}>
									Investor
								</div>

								<div
									className={`profileTypeBtn ${profileFilter === "quick_lease" && "active"}`}
									onClick={() => handleProfileFilter("quick_lease")}>
									Quick Lease
								</div>
								<div
									className={`profileTypeBtn ${profileFilter === "asset_registration" && "active"}`}
									onClick={() => handleProfileFilter("asset_registration")}>
									Asset Registration
								</div>
								<div
									className={`profileTypeBtn ${profileFilter === "insurance" && "active"}`}
									onClick={() => handleProfileFilter("insurance")}>
									Insurance
								</div>
							</div>
							{/* //edit */}
							{canCreateAsset() && profileFilter === "quick_lease" && (
								<Button variant='outlined' className='mr-3' onClick={() => navigate("/lease/create_asset")}>
									Create Asset
								</Button>
							)}
						</div>
					)}

					{!LeaseId && <LeaseCharts profileFilter={profileFilter} />}

					<FilterCategory
						currentTab={currentTab}
						tabList={getTabList(profileFilter)}
						handleClick={(category) => handleTabChange(category)}
					/>
				</PageHeader>

				<>
					{LeaseId ? (
						<Outlet context={[profileFilter, loading, setLoading]} />
					) : (
						<LeaseDataTable
							currentTab={currentTab}
							filter={profileFilter}
							TableData={TableData}
							handleDeleteLease={handleDeleteLease}
						/>
					)}
				</>
			</StyledContainer>

			{loading && (
				<Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
					<CircularProgress color='inherit' />
				</Backdrop>
			)}
		</AdminDashboardLayout>
	);
}

const getCurrentFilter = (category, searchParams) => {
	const filter = searchParams.get("filter");
	const filterList = ["personal_business", "investor", "quick_lease", "asset_registration", "insurance"];
	const investorTabList = ["created_assets", "inactive", "active", "funded"];

	if (filter) {
		return filterList.includes(filter) ? filter : "personal_business";
	} else {
		return investorTabList.includes(category) ? "investor" : "personal_business";
	}
};

export default LeasePage;
